/**
 * The readable guard on the body (combat design docs/design/combat/2026-10-11-guard-read, approved by the user 10-11).
 * Pixel rules: scene grid, master ramps (fx/pixelRules.ts FX_RAMPS), 1-px dark outline, stepped, no glow.
 * - The foe's guard tell (in place of duelBodyTell's guard / broken brackets, which hand them over here):
 *   guard = four amber corners; weak (guard points at or under `GUARD_READ.weakShare`) = the top two corners in the
 *   break's red and a small cracked shield over the head; broken = four green corners and the shield's two halves over
 *   the head, closing as the broken time runs out, then a whole shield flashing a moment when the guard comes back.
 * - Every guard broken, anyone's, in or out of a bout: a pixel heater shield over the head, whole with a white rim on
 *   the first frames, cracked, then split in two halves that fall apart with chips (0.75 s).
 */
import { GUARD_READ, type Entity, type World } from '@nine/shared';
import { FX_RAMPS } from '../fx/pixelRules';
import { globeBend } from '../globe';
import { boutGuard, guardBrokeAt } from '../guardRead';
import { duelRing, tellOf } from './duelRing';
import { registerRenderPass } from './registry';

const INK = '#0e0a10';
/** The icons' centre right of the body's middle (art px): past the brackets' 16 px half width. */
const SIDE = 25;
const GOLD = FX_RAMPS.gold!;
const AMBER = 'rgb(246,196,108)';
const RED = 'rgb(255,70,60)';
const GREEN = 'rgb(126,224,178)';
/** The words of duelRing's tell this pass draws the brackets for (duelBodyTell.ts skips them). */
const OWNED = new Set(['ตั้งรับ', 'การ์ดแตก']);
export const guardReadOwns = (word: string): boolean => OWNED.has(word);

/** The heater shield, 14 x 16 art px: 1 fill, 2 shade, 3 lit edge, 4 rib, 0 none; the outline is added round it. */
const SHIELD = (() => {
  const W = 14;
  const H = 16;
  const rows: string[] = [];
  for (let y = 0; y < H; y++) {
    let r = '';
    const half = y < 8 ? 7 : 7 * Math.sqrt(Math.max(0, 1 - ((y - 7.5) / 8.5) ** 2));
    for (let x = 0; x < W; x++) {
      const cx = x + 0.5 - 7;
      if (Math.abs(cx) > half - 0.2) r += '0';
      else if (x === 6 || x === 7) r += '4';
      else if (x > 7 && y < 8 && Math.abs(cx) > half - 1.4) r += '3';
      else r += x < 6 && y > 9 ? '2' : '1';
    }
    rows.push(r);
  }
  return rows;
})();
const CRACK = (y: number): number => 7 + (Math.floor(y / 3) % 2 ? 1 : -1) * (y % 3 === 0 ? 0 : 1);
const COLS: Record<string, string> = { '1': GOLD[3]!, '2': GOLD[2]!, '3': GOLD[5]!, '4': GOLD[4]! };

type Part = 'whole' | 'cracked' | 'left' | 'right';
/** The shield (or one half) with its top-left at x, y (screen px), `p` screen px per art px. */
function shield(g: CanvasRenderingContext2D, x: number, y: number, p: number, part: Part, rim: string = INK): void {
  const on = (sx: number, sy: number): boolean => {
    const v = SHIELD[sy]?.[sx];
    if (!v || v === '0') return false;
    const c = CRACK(sy);
    return part === 'left' ? sx < c : part === 'right' ? sx >= c : true;
  };
  // The outline: every empty cell next to a filled one.
  g.fillStyle = rim;
  for (let sy = -1; sy <= 16; sy++)
    for (let sx = -1; sx <= 14; sx++)
      if (!on(sx, sy) && (on(sx - 1, sy) || on(sx + 1, sy) || on(sx, sy - 1) || on(sx, sy + 1)))
        g.fillRect(x + sx * p, y + sy * p, p, p);
  for (let sy = 0; sy < 16; sy++)
    for (let sx = 0; sx < 14; sx++) {
      if (!on(sx, sy)) continue;
      const crackLine = part === 'cracked' && sy > 0 && (sx === CRACK(sy) || sx === CRACK(sy) - 1);
      const edge = (part === 'left' && sx === CRACK(sy) - 1) || (part === 'right' && sx === CRACK(sy));
      g.fillStyle = crackLine || edge ? INK : COLS[SHIELD[sy]![sx]!]!;
      g.fillRect(x + sx * p, y + sy * p, p, p);
    }
}

/** The small shield pip (half size: 7 x 8 cells of 2 art px drawn as 1): cracked, or split `gap` art px apart. */
function pip(g: CanvasRenderingContext2D, cx: number, y: number, p: number, part: 'whole' | 'cracked' | 'split', gap: number, rim = INK): void {
  const q = Math.max(1, Math.round(p / 2));
  const x = Math.round(cx - 7 * q);
  if (part === 'split') {
    shield(g, x - gap * p, y, q, 'left', rim);
    shield(g, x + gap * p, y, q, 'right', rim);
  } else shield(g, x, y, q, part, rim);
}

/** Per fighter: whether the guard was broken last frame and when it came back (the snap). */
const wasBroken = new Map<number, boolean>();
const backAt = new Map<number, number>();

registerRenderPass({
  id: 'guardReadTell',
  layer: 'labels',
  order: 107,
  draw({ mg, world: w, px: PX, iso, ox, oy }) {
    const p = Math.max(2, Math.round(PX));
    const at = (e: Entity, up: number): [number, number] => {
      const [sx, sy] = iso.proj(e.x, e.y);
      return [Math.round((sx + ox) * PX), Math.round(globeBend(sx + ox, sy + oy - up * e.scale)[1] * PX)];
    };
    // The shield breaking over anyone whose guard just broke.
    const near = w.entities;
    for (const e of near.values()) {
      if (e.dead) continue;
      const tau = w.time - guardBrokeAt(w, e.id);
      if (tau < 0 || tau > 0.75) continue;
      const [cx, head] = at(e, 54);
      // Beside the head, past the tell brackets: the marker, the word and the names stack over it (no overlap).
      breakIcon(mg, cx + SIDE * p, head - 2 * p, p, tau);
    }
    if (!duelRing.on) return;
    const foe = w.get(duelRing.foe);
    if (!foe || foe.dead) return;
    const tell = tellOf(foe, duelRing.stance[1], duelRing.broken[1], duelRing.recov[1]);
    const bg = boutGuard(w, foe.id);
    const broken = !!bg && bg.brokenT > 0;
    // The guard back: a whole shield with a white rim a moment.
    if (wasBroken.get(foe.id) && !broken) backAt.set(foe.id, w.time);
    wasBroken.set(foe.id, broken);
    const [cx, head] = at(foe, 54);
    const pipY = head;
    const px = cx + SIDE * p;
    const sinceBack = w.time - (backAt.get(foe.id) ?? -9);
    if (sinceBack >= 0 && sinceBack < 0.25) pip(mg, px, pipY, p, 'whole', 0, sinceBack < 0.08 ? '#fdf6ec' : INK);
    if (!tell || !guardReadOwns(tell.word)) return;
    const weak = foe.blocking && !!bg && bg.share <= GUARD_READ.weakShare;
    const col = broken ? GREEN : weak ? RED : AMBER;
    corners(mg, w, foe, PX, iso, ox, oy, col, !weak);
    if (weak) pip(mg, px, pipY, p, 'cracked', 0);
    else if (!broken) pip(mg, px, pipY, p, 'whole', 0); // a guard up: the small whole shield
    else if (broken && w.time - guardBrokeAt(w, foe.id) > 0.75)
      pip(mg, px, pipY, p, 'split', Math.round(1 + 3 * Math.min(1, bg!.brokenT / 3)));
  },
});

/** duelBodyTell's corner brackets (fully closed, steady), all four or the top two. */
function corners(
  mg: CanvasRenderingContext2D,
  _w: World,
  e: Entity,
  PX: number,
  iso: { proj(x: number, y: number): [number, number] },
  ox: number,
  oy: number,
  col: string,
  bottom: boolean,
): void {
  const [sx, sy] = iso.proj(e.x, e.y);
  const cx = (sx + ox) * PX;
  const bot = globeBend(sx + ox, sy + oy + 2)[1] * PX;
  const top = globeBend(sx + ox, sy + oy - 46 * e.scale)[1] * PX;
  const half = Math.round(16 * e.scale) * PX;
  const arm = 6 * PX;
  const t = Math.max(2, PX);
  const corner = (x: number, y: number, dx: number, dy: number) => {
    for (const [fill, grow] of [['rgba(12,9,14,.95)', t] as const, [col, 0] as const]) {
      mg.fillStyle = fill;
      const o = grow / 2;
      mg.fillRect(Math.min(x, x + dx * arm) - o, y - o, arm + grow, t + grow);
      mg.fillRect(x - o, Math.min(y, y + dy * arm) - o, t + grow, arm + grow);
    }
  };
  corner(cx - half, top, 1, 1);
  corner(cx + half - t, top, -1, 1);
  if (!bottom) return;
  corner(cx - half, bot - t, 1, -1);
  corner(cx + half - t, bot - t, -1, -1);
}

/** The break icon `tau` s after the guard broke: whole (white rim first), cracked, then the halves falling apart. */
function breakIcon(g: CanvasRenderingContext2D, cx: number, y: number, p: number, tau: number): void {
  const x = Math.round(cx - 7 * p);
  if (tau < 0.2) {
    shield(g, x, y, p, tau < 0.06 ? 'whole' : 'cracked', tau < 0.06 ? '#fdf6ec' : INK);
    return;
  }
  // Stepped at 12 fps, as the other pixel FX fall.
  const u = Math.floor((tau - 0.2) * 12) / 12;
  const dx = Math.round(18 * u);
  const dy = Math.round(70 * u * u + 6 * u);
  shield(g, x - dx * p, y + dy * p, p, 'left');
  shield(g, x + dx * p, y + (dy + Math.round(2 * u)) * p, p, 'right');
  // Chips: five steel-dark specks flung out and down.
  for (let i = 0; i < 5; i++) {
    const a = -Math.PI * 0.9 + i * 0.45;
    const r = 6 + 40 * u;
    const px = Math.round(cx / p + Math.cos(a) * r) * p;
    const py = Math.round(y / p + 8 + Math.sin(a) * r * 0.6 + 60 * u * u) * p;
    g.fillStyle = INK;
    g.fillRect(px - p, py - p, 3 * p, 3 * p);
    g.fillStyle = GOLD[5]!;
    g.fillRect(px, py, p, p);
  }
}
